import { withFormik } from 'formik';
import * as yup from 'yup';
import SparkTelephonyDeployment from './SparkTelephonyDeployment';
import TelephonyDeploymentRequest from '../../../../shared/models/telephonyDeployment/TelephonyDeploymentRequest';
import { defaultTelephonyDeploymentSettings } from '../../../../shared/constants/telephonyConstants.tsx/defaultTelephonyDeploymentSettings';
import { AppendedSection } from './ApplicationManagerContent';
import { SPANISH_GRACEFUL_HANGUP_FINAL_MESSAGE, SPANISH_GRACEFUL_HANGUP_WARNING_MESSAGE, SpanishLineWarningProps, runSpanishSetupStep, setupSpanishInteractionModel } from '../../services/spanishLineSetup';
import { spanishTtsVoice, englishMultilingualTtsVoice } from '../../../../shared/constants/sparkConstants/voices';
import { OutboundSchedulePolicy, defaultOutboundSchedulePolicy, runOutboundProvisioning } from '../../services/outboundAutomation';
import { findVoiceOptionByValue, getVoiceStringForLocale } from './sparkVoiceLanguage';
import { defaultVoiceOption } from '../../../../shared/constants/sparkConstants/voiceOptions';
import { carryVoiceStringPrompt } from '../../../../shared/utils/ttsVoiceString';

export interface SparkTelephonyDeploymentProps extends SpanishLineWarningProps {
    voiceOptions: any
    handlePreviousSection: () => void
    handleNextSection: () => void
    createTelephonyDeployment: (appId: string, telephonyDeploymentRequest: TelephonyDeploymentRequest) => Promise<any>
    applicationId: string
    environmentId: string
    appendedSections: AppendedSection[]
    telephonyDeploymentSection: AppendedSection
    setAppendedSections: (sections: AppendedSection[]) => void
    incompleteSections: any[]
    setIncompleteSections: (sections: any[]) => void
    getVoiceString: (voice: string) => string
    clearTelephonyDeploymentErrors: () => void
    setPageErrorText: (text: string) => void
    phoneNumberDeploymentErrorText: string
    isPhoneSetupPending?: boolean
    setIsPhoneSetupPending?: (pending: boolean) => void
    setPhoneNumberDeploymentErrorText: (text: string) => void
    getTabs: (existingTelephonyDeployment?: boolean) => void
    setTabs: (tabs: any) => void
    loadTelephonyDeployments: () => void
    handleRemoveSection: (sectionToRemove: AppendedSection) => void
    removeSectionFromIncompleteSections: (sectionToRemove: AppendedSection) => void
    assistantType?: string
    orgName?: string
    appName?: string
    onMultilingualEnabled?: () => Promise<void>
    isOutboundEnabledOnAnyDeployment?: boolean
    orgId?: string
    applicationFeatureFlags?: FeatureFlagModel[]
}

export interface SparkTelephonyDeploymentFormData {
    phoneNumberStartsWith: string
    callRecordingEnabled: boolean
    ttsVoice: string
    /** Optional style prompt for voices that support one (Gemini). */
    ttsVoicePrompt?: string
    useVirtualNumber: boolean
    virtualNumber?: string
    addSpanishLine: boolean
    addOutbound: boolean
    outboundSaved: boolean
    outboundSchedulePolicy: OutboundSchedulePolicy
    outboundCallingEnabled: boolean
    outboundSetupMode: 'manual' | 'clone'
    outboundSourceApplicationId: string
}

export const getDefaultVoice = (assistantType?: string): string => defaultVoiceOption.value;

export const submitSparkTelephonyDeployment = async (values: SparkTelephonyDeploymentFormData, props: SparkTelephonyDeploymentProps): Promise<boolean> => {

    if (values.addSpanishLine) {
        props.clearSpanishSetupWarnings();
    }

    // Build the en-US deployment name
    const enUsName = (props.orgName && props.appName)
        ? `${props.orgName} - ${props.appName} - EN`
        : defaultTelephonyDeploymentSettings.name;

    const isDental = props.assistantType === 'dental-scheduling';
    const isOrdering = props.assistantType === 'ai-ordering';
    const selectedVoiceOption = findVoiceOptionByValue(props.voiceOptions, values.ttsVoice);
    const configuredEnglishVoice = carryVoiceStringPrompt(getVoiceStringForLocale(
        selectedVoiceOption,
        'en-US',
        values.addSpanishLine
            ? englishMultilingualTtsVoice
            : (props.getVoiceString(values.ttsVoice) || defaultTelephonyDeploymentSettings.ttsOptions[0].ttsVoice)
    ), values.ttsVoicePrompt);
    const configuredSpanishVoice = carryVoiceStringPrompt(getVoiceStringForLocale(selectedVoiceOption, 'es-US', spanishTtsVoice), values.ttsVoicePrompt);
    const configuredProvider = selectedVoiceOption?.provider || defaultTelephonyDeploymentSettings.ttsOptions[0].ttsProvider;

    const telephonyDeploymentRequest: TelephonyDeploymentRequest = {
        environmentId: props.environmentId,
        noTracking: defaultTelephonyDeploymentSettings.noTracking,
        phoneNumberStartsWith: values.useVirtualNumber ? null : (values.phoneNumberStartsWith ?? defaultTelephonyDeploymentSettings.phoneNumberStartsWith),
        ttsOptions: [{
            ttsProvider: configuredProvider,
            ttsVoice: configuredEnglishVoice
        }],
        locale: defaultTelephonyDeploymentSettings.locale,
        asrEndpointing: defaultTelephonyDeploymentSettings.asrEndpointing,
        asrProvider: defaultTelephonyDeploymentSettings.asrProvider,
        // When adding Spanish line, upgrade en-US to nova-3
        asrModel: values.addSpanishLine ? 'nova-3' : defaultTelephonyDeploymentSettings.asrModel,
        asrTier: defaultTelephonyDeploymentSettings.asrTier,
        sonioxContextJson: defaultTelephonyDeploymentSettings.sonioxContextJson,
        sonioxEndpointSensitivity: defaultTelephonyDeploymentSettings.sonioxEndpointSensitivity,
        sonioxMaxEndpointDelayMS: defaultTelephonyDeploymentSettings.sonioxMaxEndpointDelayMS,
        performLanguageDetection: defaultTelephonyDeploymentSettings.performLanguageDetection,
        dtmfTurnsToListen: values.addSpanishLine ? 2 : defaultTelephonyDeploymentSettings.dtmfTurnsToListen,
        ttsPronunciationsMap: defaultTelephonyDeploymentSettings.ttsPronunciationsMap,
        firstMessageDelayMS: defaultTelephonyDeploymentSettings.firstMessageDelayMS,
        mergeTurnsAfterPauses: defaultTelephonyDeploymentSettings.mergeTurnsAfterPauses,
        mergeTurnTimeoutMS: defaultTelephonyDeploymentSettings.mergeTurnTimeoutMS,
        disableBargeIn: defaultTelephonyDeploymentSettings.disableBargeIn,
        repromptAttempts: defaultTelephonyDeploymentSettings.repromptAttempts,
        repromptDelaySeconds: defaultTelephonyDeploymentSettings.repromptDelaySeconds,
        name: enUsName,
        proactiveResponseDelayMS: isDental ? 1900 : defaultTelephonyDeploymentSettings.proactiveResponseDelayMS,
        proactiveResponses: defaultTelephonyDeploymentSettings.proactiveResponses,
        useProactiveResponses: defaultTelephonyDeploymentSettings.useProactiveResponses,
        disableWelcomeMessageBargeIn: defaultTelephonyDeploymentSettings.disableWelcomeMessageBargeIn,
        disableSpeechStarted: defaultTelephonyDeploymentSettings.disableSpeechStarted,
        speechStartedUnconfirmedBargeInLimit: defaultTelephonyDeploymentSettings.speechStartedUnconfirmedBargeInLimit,
        speechStartedPauseMS: defaultTelephonyDeploymentSettings.speechStartedPauseMS,
        speechStartedRequiredBufferCount: defaultTelephonyDeploymentSettings.speechStartedRequiredBufferCount,
        callRecordingEnabled: values.callRecordingEnabled ?? defaultTelephonyDeploymentSettings.callRecordingEnabled,
        speechStartedLevel: defaultTelephonyDeploymentSettings.speechStartedLevel,
        virtualNumber: values.useVirtualNumber && defaultTelephonyDeploymentSettings.virtualNumber,
        useVirtualNumber: values.useVirtualNumber,
        useGenerativeProactiveResponse: defaultTelephonyDeploymentSettings.useGenerativeProactiveResponse,
        generativeProactiveResponsePrompt: isDental
            ? '{{constant.generative_proactive_response_dental}}'
            : isOrdering
                ? '{{constant.proactive_response_prompt}}'
                : defaultTelephonyDeploymentSettings.generativeProactiveResponsePrompt,
        lastProactiveResponsesToDisallow: defaultTelephonyDeploymentSettings.lastProactiveResponsesToDisallow,
        notificationEmails: defaultTelephonyDeploymentSettings.notificationEmails,
        platform: defaultTelephonyDeploymentSettings.platform,
        postProactiveResponsePauseDelayMS: isDental ? 500 : defaultTelephonyDeploymentSettings.postProactiveResponsePauseDelayMS,
        enableBackgroundOverlayAudio: defaultTelephonyDeploymentSettings.enableBackgroundOverlayAudio,
        backgroundOverlayAudioUrl: defaultTelephonyDeploymentSettings.backgroundOverlayAudioUrl,
        backgroundOverlayAudioLeadInMS: defaultTelephonyDeploymentSettings.backgroundOverlayAudioLeadInMS,
    };

    const createTelephonyDeploymentResponse = await props.createTelephonyDeployment(props.applicationId, telephonyDeploymentRequest);

    if (createTelephonyDeploymentResponse?.resultType === 'Ok' && createTelephonyDeploymentResponse.data) {
        if (values.addSpanishLine) {
            const provisionedPhoneNumber = createTelephonyDeploymentResponse.data.phoneNumber;

            await setupSpanishInteractionModel(props.applicationId, props.onSpanishSetupWarning);

            const esUsName = (props.orgName && props.appName)
                ? `${props.orgName} - ${props.appName} - ES`
                : '';

            const esUsRequest: TelephonyDeploymentRequest = {
                ...createTelephonyDeploymentResponse.data,
                phoneNumberStartsWith: provisionedPhoneNumber,
                locale: 'es-US',
                asrModel: 'nova-2',
                performLanguageDetection: false,
                dtmfTurnsToListen: 0,
                name: esUsName,
                ttsOptions: [{
                    ttsProvider: selectedVoiceOption ? configuredProvider : 'ElevenLabs',
                    ttsVoice: configuredSpanishVoice
                }],
                proactiveResponses: 'Un momento|Veamos|Solo un momento|Espera un segundo|Espera mientras reviso eso|Dame un momento|Espera mientras miro eso',
                followUpProactiveResponses: 'Todavía estoy revisando eso | Sigo investigando esto por ti | Estoy trabajando activamente en esto y te actualizaré pronto | Todavía estoy buscando | Estoy avanzando con esto y te mantendré informado',
                useGenerativeProactiveResponse: true,
                generativeProactiveResponsePrompt: isOrdering
                    ? '{{constant.generative_proactive_response_ordering_spanish}}'
                    : '{{constant.generative_proactive_response_dental_spanish}}',
                maxProactiveResponseAttempts: 3,
                useGracefulRepromptHangup: true,
                gracefulHangupWarningMessage: SPANISH_GRACEFUL_HANGUP_WARNING_MESSAGE,
                gracefulHangupFinalMessage: SPANISH_GRACEFUL_HANGUP_FINAL_MESSAGE,
            };

            const spanishLineResult = await runSpanishSetupStep('line deployment', () =>
                props.createTelephonyDeployment(props.applicationId, esUsRequest), props.onSpanishSetupWarning);

            // Only dental templates have a multilingual form field.
            if (spanishLineResult && props.assistantType === 'dental-scheduling') {
                await props.onMultilingualEnabled?.();
            }
        }

        if (values.addOutbound && props.assistantType === 'dental-scheduling') {
            const outboundResult = await runOutboundProvisioning({
                applicationId: props.applicationId,
                environmentId: props.environmentId || '',
                organizationName: props.orgName || '',
                locationName: props.appName || '',
                appName: props.appName || '',
                schedulePolicy: values.outboundSchedulePolicy || defaultOutboundSchedulePolicy,
                hasSpanish: values.addSpanishLine,
                outboundCallingEnabled: values.outboundCallingEnabled,
                orgId: props.orgId,
                sourceApplicationId: values.outboundSetupMode === 'clone' ? values.outboundSourceApplicationId : undefined
            });

            if (!outboundResult.success) {
                props.setPageErrorText(outboundResult.errorMsg || 'We could not save the outbound setup. Please try again.');
                return false;
            }
        }

        await props.loadTelephonyDeployments();

        const updatedTabs = props.getTabs(true);
        props.setTabs(updatedTabs);

        // Advance only after all phone setup requests and refreshes have settled.
        props.removeSectionFromIncompleteSections(props.telephonyDeploymentSection);
        props.handleRemoveSection(props.telephonyDeploymentSection);
        return true;
    };

    if (createTelephonyDeploymentResponse?.resultType !== 'Ok') {
        const incompleteTelephonyDeploymentSection = { ...props.telephonyDeploymentSection, isComplete: false };
        if (createTelephonyDeploymentResponse.errors[0] === "No numbers found") {
            props.setPageErrorText("Uh oh! It looks like the area code you chose isn't available. Please save your changes, exit, and reopen the form trying a different one.");
        } else {
            props.setPageErrorText("Uh oh! It looks like there was a problem creating your phone number. Please save your changes, exit, and try again in a few minutes.");
        }
        const telephonyDeploymentSectionIndex = props.incompleteSections?.findIndex((section) => {
            return section.id === incompleteTelephonyDeploymentSection.id
        });
        const incompleteSectionsCopy = [...props.incompleteSections];
        if (telephonyDeploymentSectionIndex !== -1) {
            incompleteSectionsCopy[telephonyDeploymentSectionIndex] = incompleteTelephonyDeploymentSection;
            props.setIncompleteSections(incompleteSectionsCopy);
        } else {
            props.setIncompleteSections([...incompleteSectionsCopy, incompleteTelephonyDeploymentSection]);
        }
    }
    return false;
};

export const handleSparkTelephonyFormSubmit = async (
    values: SparkTelephonyDeploymentFormData,
    props: SparkTelephonyDeploymentProps,
    setSubmitting: (submitting: boolean) => void
) => {
    let completed = false;
    props.setIsPhoneSetupPending?.(true);
    try {
        completed = await submitSparkTelephonyDeployment(values, props);
    } finally {
        if (!completed) props.setIsPhoneSetupPending?.(false);
        setSubmitting(false);
    }
};

const SparkTelephonyDeploymentForm = withFormik<SparkTelephonyDeploymentProps, SparkTelephonyDeploymentFormData>({
    mapPropsToValues: props => (
        {
            phoneNumberStartsWith: '',
            callRecordingEnabled: true,
            ttsVoice: getDefaultVoice(props.assistantType),
            ttsVoicePrompt: '',
            useVirtualNumber: false,
            virtualNumber: '',
            addSpanishLine: false,
            addOutbound: false,
            outboundSaved: false,
            outboundSchedulePolicy: defaultOutboundSchedulePolicy,
            outboundCallingEnabled: false,
            outboundSetupMode: 'manual' as 'manual',
            outboundSourceApplicationId: ''
        }
    ),
    validationSchema: yup.object().shape({
        outboundSaved: yup.boolean().test(
            'outbound-validation',
            'An outbound schedule is required to continue. Either uncheck "Add Outbound" or "Set the Schedule" to proceed.',
            function (value) {
                const { addOutbound } = this.parent;
                if (addOutbound && !value) {
                    return false;
                }
                return true;
            }
        )
    }),
    handleSubmit: async (values, { props, setSubmitting }) => {
        await handleSparkTelephonyFormSubmit(values, props, setSubmitting);
    }
})(SparkTelephonyDeployment);


export default SparkTelephonyDeploymentForm;
